import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from 'components/Accordion';
import Button from 'components/DesignSystem/Button';
import DatePicker from 'components/DatePicker';
import RadioButton from 'components/DesignSystem/RadioButton';
import {
  selectMatrizDataAnalisada,
  selectMatrizCenario,
  setDataAnalisada,
  setCenario,
} from 'redux/reducers/relatoriosMatrizReducer';
import { MatrizCenario } from 'types/Relatorios/Matriz';

import './index.scss';

const CENARIOS: { value: MatrizCenario; label: string }[] = [
  { value: 'base', label: 'Base' },
  { value: 'controle', label: 'Controle' },
  { value: 'sensibilidade_lrcp', label: 'Sensibilidade LRCP' },
  { value: 'sensibilidade_cvar', label: 'Sensibilidade CVAR' },
];

/**
 * Painel de controle da Matriz (Informe Semanal). Espelha o `SemanalControlPanel`
 * (mesmo padrão do Figma: Accordion do design system + DatePicker inline para
 * a Data Analisada, radios fechados Base/Controle/Sensibilidade LRCP/Sensibilidade
 * CVAR para o Cenário). Rascunho local (useState) só faz commit ao slice no clique
 * em "Atualizar Data/Cenário".
 */
export default function MatrizControlPanel() {
  const dispatch = useDispatch();
  const dataAnalisada = useSelector(selectMatrizDataAnalisada);
  const cenarioAplicado = useSelector(selectMatrizCenario);

  const [draftData, setDraftData] = useState(
    () => new Date(dataAnalisada.ano, dataAnalisada.mes - 1, dataAnalisada.dia),
  );
  const [draftCenario, setDraftCenario] = useState<MatrizCenario>(cenarioAplicado);

  const handleDataChange = useCallback((date: Date | null) => {
    if (date) setDraftData(date);
  }, []);

  const handleCenarioChange = useCallback((value: MatrizCenario) => () => {
    setDraftCenario(value);
  }, []);

  const handleAtualizar = useCallback(() => {
    dispatch(setDataAnalisada({
      mes: draftData.getMonth() + 1,
      ano: draftData.getFullYear(),
      dia: draftData.getDate(),
    }));
    dispatch(setCenario(draftCenario));
  }, [dispatch, draftData, draftCenario]);

  return (
    <div className="matriz-control-panel">
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>Data Analisada</Accordion.Header>
          <Accordion.Body className="d-grid">
            <DatePicker
              inline
              selected={draftData}
              onChange={handleDataChange}
              maxDate={new Date()}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>Cenário</Accordion.Header>
          <Accordion.Body className="d-grid gap-1">
            {CENARIOS.map(({ value, label }) => (
              <RadioButton
                key={value}
                name="matriz-cenario"
                label={label}
                value={value}
                checked={draftCenario === value}
                onChange={handleCenarioChange(value)}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Button onClick={handleAtualizar} className="matriz-control-panel__atualizar">
        Atualizar Data/Cenário
      </Button>
    </div>
  );
}
